/**
 * RegistrationModal - Direct registration form in a modal
 * 
 * Opens directly from "Get Started Free" and other conversion CTA buttons
 * Contains the full registration form (matching AuthPage registration)
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAuth } from '../contexts/AuthContext';
import { useNotification } from '../contexts/NotificationContext';
import api from '../lib/api';
import { UserPlus, Mail, Lock, User, Phone, MapPin, Globe, X, Tag } from 'lucide-react';

// Format phone as user types (for input field)
const formatPhoneInput = (value) => {
  if (!value) return '';
  const cleaned = value.replace(/\D/g, '');
  if (cleaned.length <= 3) {
    return cleaned;
  } else if (cleaned.length <= 6) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
  } else if (cleaned.length <= 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }
  return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
};

// Empty form state - used for initialization and reset
const EMPTY_FORM = {
  email: '',
  password: '',
  first_name: '',
  last_name: '',
  phone: '',
  alternate_email: '',
  city: '',
  state: '',
  country: 'United States',
  promo_code: ''
};

const RegistrationModal = ({ isOpen, onClose, onSuccess, promoCode }) => {
  const navigate = useNavigate();
  const { loginWithToken, getHomePage } = useAuth();
  const { notifySuccess, notifyError } = useNotification();
  const [loading, setLoading] = useState(false);
  
  // Form state - always starts empty
  const [formData, setFormData] = useState({ ...EMPTY_FORM });
  const [errors, setErrors] = useState({});

  // Registration duplicate-detection warning & verification gate state
  const [emailWarning, setEmailWarning] = useState(null);
  const [verificationPending, setVerificationPending] = useState(null);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendMessage, setResendMessage] = useState(null);

  // CRITICAL: Reset form whenever modal opens to prevent data leakage
  useEffect(() => {
    if (isOpen) {
      setFormData({
        ...EMPTY_FORM,
        promo_code: promoCode || ''
      });
      setErrors({});
    }
  }, [isOpen, promoCode]);

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    // Format phone number as user types
    let formattedValue = value;
    if (name === 'phone') {
      formattedValue = formatPhoneInput(value);
    } else if (name === 'email' || name === 'alternate_email') {
      // Section 9.1: trim + lowercase email fields
      formattedValue = value.trim().toLowerCase();
    }
    setFormData(prev => ({
      ...prev,
      [name]: formattedValue
    }));
    if (name === 'email' && emailWarning) {
      setEmailWarning(null);
    }
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const handleResendVerification = async (emailToResend) => {
    setResendLoading(true);
    setResendMessage(null);
    try {
      await api.post('/auth/resend-verification', { email: emailToResend });
      setResendMessage('If an unverified account exists, a new verification link has been sent.');
    } catch (err) {
      setResendMessage('If an unverified account exists, a new verification link has been sent.');
    } finally {
      setResendLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Invalid email format';
    }
    
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }
    
    // Validate registration fields
    if (!formData.first_name?.trim()) newErrors.first_name = 'First name is required';
    if (!formData.last_name?.trim()) newErrors.last_name = 'Last name is required';
    if (!formData.phone?.trim()) newErrors.phone = 'Phone number is required';
    if (!formData.alternate_email) {
      newErrors.alternate_email = 'Alternate email is required';
    } else if (!validateEmail(formData.alternate_email)) {
      newErrors.alternate_email = 'Invalid email format';
    }
    if (!formData.city?.trim()) newErrors.city = 'City is required';
    if (!formData.state?.trim()) newErrors.state = 'State is required';
    if (!formData.country?.trim()) newErrors.country = 'Country is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    
    try {
      // Register the user
      const registerResponse = await api.post('/auth/register', {
        email: formData.email,
        password: formData.password,
        first_name: formData.first_name,
        last_name: formData.last_name,
        phone: formData.phone,
        alternate_email: formData.alternate_email,
        city: formData.city,
        state: formData.state,
        country: formData.country,
        promo_code: formData.promo_code.trim() || null
      });
      
      const { access_token, user: registeredUser } = registerResponse.data;

      // Surface duplicate-detection warning if backend flagged similar email/name
      if (registeredUser?.registration_warning) {
        setEmailWarning(registeredUser.registration_warning);
      }

      // Section 9.3: verification gate — show verification-pending screen
      // instead of logging into dashboard when backend returned verification_pending.
      if (registeredUser?.verification_pending) {
        setVerificationPending({ email: registeredUser.email });
        setFormData({ ...EMPTY_FORM });
        return; // Do not auto-login or navigate
      }

      const userData = await loginWithToken(access_token);
      
      // Check if promo code was applied and show branded modal for bonus credits
      if (registeredUser?.promo_code_applied) {
        notifySuccess(
          `Your promo code has been applied! You've received ${registeredUser.credits_balance} startup credits to get started.`,
          'Welcome to NewAudia!'
        );
      }
      // Regular welcome is silent - the UI reflects the successful state
      
      // Reset form
      setFormData({ ...EMPTY_FORM });
      
      // Close modal
      onClose();
      
      // Call success callback if provided
      if (onSuccess) {
        onSuccess(userData);
      } else {
        // Default: navigate to reader home
        const homePage = getHomePage(userData);
        navigate(homePage);
      }
      
    } catch (error) {
      console.error('Registration error:', error);
      if (error.response?.status === 400) {
        const detail = error.response?.data?.detail;
        if (detail?.includes('already registered') || detail?.includes('already exists')) {
          setErrors({ email: 'This email is already registered. Please sign in instead.' });
        } else {
          // Show error in form, not as toast
          setErrors({ submit: detail || 'Registration failed. Please check your information.' });
        }
      } else {
        notifyError('We couldn\'t complete your registration. Please try again.', 'Registration Failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSignInInstead = () => {
    onClose();
    navigate('/login');
  };

  const handleClose = () => {
    // Reset form on close - prevents data persistence
    setFormData({ ...EMPTY_FORM });
    setErrors({});
    setEmailWarning(null);
    setVerificationPending(null);
    setResendMessage(null);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="w-[96vw] max-w-md max-h-[95vh] overflow-y-auto p-0 bg-white">
        {/* Header with Logo */}
        <div className="sticky top-0 bg-gradient-to-r from-purple-600 to-indigo-600 px-5 py-4 text-center">
          <img 
            src="/newaudia-logo.jpg" 
            alt="NewAudia™" 
            className="h-10 w-auto mx-auto rounded-lg shadow-lg mb-2"
          />
          <h2 className="text-lg font-bold text-white">
            Create Your Free Account
          </h2>
          <p className="text-xs text-purple-100">
            Get 10 complimentary Reader Credits
          </p>
        </div>

        {verificationPending ? (
          <div data-testid="reg-modal-verification-pending" className="p-6 text-center">
            <div className="mx-auto mb-4 w-14 h-14 rounded-full bg-green-100 flex items-center justify-center">
              <Mail className="w-7 h-7 text-green-600" />
            </div>
            <h3 className="text-base font-bold text-gray-900 mb-2">
              Verify Your Email
            </h3>
            <p className="text-sm text-gray-700 mb-2">
              We've sent a verification link to <span className="font-semibold">{verificationPending.email}</span>.
            </p>
            <p className="text-xs text-gray-600 mb-4">
              Please check your inbox and click the link to activate your account and receive your Reader Credits. The link expires in 24 hours.
            </p>
            <Button
              data-testid="reg-modal-resend-verification-btn"
              type="button"
              variant="outline"
              disabled={resendLoading}
              onClick={() => handleResendVerification(verificationPending.email)}
              className="text-sm"
            >
              {resendLoading ? 'Sending…' : 'Resend verification email'}
            </Button>
            {resendMessage && (
              <p className="text-xs text-gray-600 mt-3" data-testid="reg-modal-resend-message">
                {resendMessage}
              </p>
            )}
            <div className="mt-5">
              <Button
                type="button"
                onClick={handleClose}
                className="w-full"
                data-testid="reg-modal-close-after-verify-btn"
              >
                Close
              </Button>
            </div>
          </div>
        ) : (
        <form onSubmit={handleSubmit} className="p-5 space-y-3" autoComplete="off">
          {/* Personal Information */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                First Name *
              </label>
              <div className="relative">
                <User className="absolute left-2.5 top-1/2 transform -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                <Input
                  type="text"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleInputChange}
                  autoComplete="new-password"
                  className={`pl-8 text-sm h-9 ${errors.first_name ? 'border-red-500' : ''}`}
                  placeholder="John"
                />
              </div>
              {errors.first_name && (
                <p className="text-red-500 text-[10px] mt-0.5">{errors.first_name}</p>
              )}
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Last Name *
              </label>
              <div className="relative">
                <User className="absolute left-2.5 top-1/2 transform -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                <Input
                  type="text"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleInputChange}
                  autoComplete="new-password"
                  className={`pl-8 text-sm h-9 ${errors.last_name ? 'border-red-500' : ''}`}
                  placeholder="Doe"
                />
              </div>
              {errors.last_name && (
                <p className="text-red-500 text-[10px] mt-0.5">{errors.last_name}</p>
              )}
            </div>
          </div>

          {/* Phone */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Phone *
            </label>
            <div className="relative">
              <Phone className="absolute left-2.5 top-1/2 transform -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
              <Input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                autoComplete="new-password"
                className={`pl-8 text-sm h-9 ${errors.phone ? 'border-red-500' : ''}`}
                placeholder="+1 (555) 123-4567"
              />
            </div>
            {errors.phone && (
              <p className="text-red-500 text-[10px] mt-0.5">{errors.phone}</p>
            )}
          </div>

          {/* Email */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Email *
            </label>
            <div className="relative">
              <Mail className="absolute left-2.5 top-1/2 transform -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
              <Input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                autoComplete="new-password"
                className={`pl-8 text-sm h-9 ${errors.email ? 'border-red-500' : ''}`}
                placeholder="you@example.com"
              />
            </div>
            {errors.email && (
              <p className="text-red-500 text-[10px] mt-0.5">{errors.email}</p>
            )}
            {emailWarning && (
              <div
                data-testid="registration-modal-warning-notice"
                className="mt-2 px-3 py-2 rounded border border-yellow-300 bg-yellow-50 text-yellow-900 text-[11px]"
              >
                {emailWarning}
              </div>
            )}
          </div>

          {/* Alternate Email */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Alt Email (Recovery) *
            </label>
            <div className="relative">
              <Mail className="absolute left-2.5 top-1/2 transform -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
              <Input
                type="email"
                name="alternate_email"
                value={formData.alternate_email}
                onChange={handleInputChange}
                autoComplete="new-password"
                className={`pl-8 text-sm h-9 ${errors.alternate_email ? 'border-red-500' : ''}`}
                placeholder="recovery@example.com"
              />
            </div>
            {errors.alternate_email && (
              <p className="text-red-500 text-[10px] mt-0.5">{errors.alternate_email}</p>
            )}
          </div>

          {/* Password */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Password *
            </label>
            <div className="relative">
              <Lock className="absolute left-2.5 top-1/2 transform -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
              <Input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                autoComplete="new-password"
                className={`pl-8 text-sm h-9 ${errors.password ? 'border-red-500' : ''}`}
                placeholder="••••••••"
              />
            </div>
            {errors.password && (
              <p className="text-red-500 text-[10px] mt-0.5">{errors.password}</p>
            )}
          </div>

          {/* Location - City larger, State smaller, Country default */}
          <div className="grid grid-cols-6 gap-2">
            <div className="col-span-3">
              <label className="block text-xs font-medium text-gray-700 mb-1">
                City *
              </label>
              <div className="relative">
                <MapPin className="absolute left-2.5 top-1/2 transform -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                <Input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  autoComplete="new-password"
                  className={`pl-8 text-sm h-9 ${errors.city ? 'border-red-500' : ''}`}
                  placeholder="City"
                />
              </div>
              {errors.city && (
                <p className="text-red-500 text-[10px] mt-0.5">{errors.city}</p>
              )}
            </div>

            <div className="col-span-1">
              <label className="block text-xs font-medium text-gray-700 mb-1">
                State *
              </label>
              <div className="relative">
                <Input
                  type="text"
                  name="state"
                  value={formData.state}
                  onChange={handleInputChange}
                  autoComplete="new-password"
                  className={`text-sm h-9 ${errors.state ? 'border-red-500' : ''}`}
                  placeholder="State"
                />
              </div>
              {errors.state && (
                <p className="text-red-500 text-[10px] mt-0.5">{errors.state}</p>
              )}
            </div>

            <div className="col-span-2">
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Country *
              </label>
              <div className="relative">
                <Globe className="absolute left-2.5 top-1/2 transform -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                <Input
                  type="text"
                  name="country"
                  value={formData.country}
                  onChange={handleInputChange}
                  autoComplete="new-password"
                  className={`pl-8 text-sm h-9 ${errors.country ? 'border-red-500' : ''}`}
                  placeholder="Country"
                />
              </div>
              {errors.country && (
                <p className="text-red-500 text-[10px] mt-0.5">{errors.country}</p>
              )}
            </div>
          </div>

          {/* Promo Code (Optional) */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Promo Code (optional)
            </label>
            <div className="relative">
              <Tag className="absolute left-2.5 top-1/2 transform -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
              <Input
                type="text"
                name="promo_code"
                value={formData.promo_code}
                onChange={handleInputChange}
                autoComplete="off"
                className="pl-8 text-sm h-9 uppercase"
                placeholder="Enter promo code"
                data-testid="promo-code-input"
              />
            </div>
            <p className="text-[10px] text-gray-500 mt-0.5">Have a promo code? Enter it for bonus credits!</p>
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white font-semibold py-2.5 mt-2"
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Creating Account...
              </span>
            ) : (
              <span className="flex items-center justify-center gap-2">
                <UserPlus className="w-4 h-4" />
                Create Free Account
              </span>
            )}
          </Button>

          {/* Sign In Link */}
          <div className="text-center pt-2 border-t border-gray-100">
            <p className="text-xs text-gray-600">
              Already have an account?{' '}
              <button
                type="button"
                onClick={handleSignInInstead}
                className="text-purple-600 hover:text-purple-700 font-semibold"
              >
                Sign In
              </button>
            </p>
          </div>

          {/* Cancel Button */}
          <Button
            type="button"
            onClick={handleClose}
            data-testid="registration-modal-cancel-btn"
            className="w-full bg-red-600 hover:bg-red-700 text-white font-bold py-2.5 mt-3"
          >
            Cancel
          </Button>
        </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default RegistrationModal;
